import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, CircularProgress,
  IconButton, Chip, Stack, Alert, Tooltip, Paper, Divider, Checkbox,
  FormControlLabel, FormGroup, Button, List, ListItemButton,
} from '@mui/material';
import {
  Close, AccountTree, ZoomIn, ZoomOut, CenterFocusStrong, Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import { grey, blue, green, orange, purple, red, teal } from '@mui/material/colors';
import ForceGraph2D from 'react-force-graph-2d';
import { loadProjectEntityGraph } from '../../api/projectApi';

const TYPE_COLORS_HEX = {
  Class_Project: '#1565C0',
  Class_Ontology: '#42A5F5',
  Requirement_Project: '#2E7D32',
  Requirement_Ontology: '#66BB6A',
  VerificationPlan_Project: '#7B1FA2',
  VerificationPlan_Ontology: '#AB47BC',
  Object: '#E65100',
  Project: '#C62828',
  Ontology: '#546E7A',
  Superclass_External: '#00796B',
};

const TYPE_COLORS_MUI = {
  Class_Project: blue[800],
  Class_Ontology: blue[400],
  Requirement_Project: green[800],
  Requirement_Ontology: green[400],
  VerificationPlan_Project: purple[800],
  VerificationPlan_Ontology: purple[400],
  Object: orange[800],
  Project: red[700],
  Ontology: grey[700],
  Superclass_External: teal[700],
};

const TYPE_SHORT = {
  Class_Project: 'CLS-P',
  Class_Ontology: 'CLS-O',
  Requirement_Project: 'REQ-P',
  Requirement_Ontology: 'REQ-O',
  VerificationPlan_Project: 'VP-P',
  VerificationPlan_Ontology: 'VP-O',
  Object: 'OBJ',
  Project: 'PRJ',
  Ontology: 'ONT',
  Superclass_External: 'SUPER',
};

const EDGE_COLORS = { cross: '#FF6F00', same: '#90A4AE' };

const humanize = (t) => (t || '').replace(/_/g, ' ');

const isCrossSourceEdge = (parentType, childType) => {
  const pIsP = parentType.endsWith('_Project') || parentType === 'Project';
  const cIsO = childType.endsWith('_Ontology') || childType === 'Ontology';
  const pIsO = parentType.endsWith('_Ontology') || parentType === 'Ontology';
  const cIsP = childType.endsWith('_Project') || childType === 'Project';
  return (pIsP && cIsO) || (pIsO && cIsP);
};

const EntityGraphDialog = ({ open, onClose, projectId, projectName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [visibleTypes, setVisibleTypes] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const graphRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setDimensions({ width, height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [open, rawData, isFullscreen]);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRawData(null);
    setSelectedNode(null);

    loadProjectEntityGraph(projectId)
      .then((data) => {
        if (cancelled) return;
        setRawData(data);
        const types = {};
        for (const n of data.nodes) types[n.entity_type] = true;
        setVisibleTypes(types);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'Error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const forceGraphData = useMemo(() => {
    if (!rawData) return { nodes: [], links: [] };
    const visibleNodeIds = new Set();
    const nodes = [];
    for (const n of rawData.nodes) {
      if (!visibleTypes[n.entity_type]) continue;
      const nodeId = `${n.entity_type}:${n.id}`;
      visibleNodeIds.add(nodeId);
      nodes.push({
        id: nodeId, dbId: n.id, name: n.name,
        label: n.label || n.name || '', notation: n.notation || '',
        entity_type: n.entity_type, uri: n.uri || '',
        color: TYPE_COLORS_HEX[n.entity_type] || '#78909C',
        displayLabel: n.notation || n.name || n.label || String(n.id),
      });
    }
    const links = [];
    for (const e of rawData.edges) {
      const sourceId = `${e.parent_type}:${e.parent_id}`;
      const targetId = `${e.child_type}:${e.child_id}`;
      if (!visibleNodeIds.has(sourceId) || !visibleNodeIds.has(targetId)) continue;
      links.push({
        source: sourceId, target: targetId,
        parent_type: e.parent_type, child_type: e.child_type,
        cross: isCrossSourceEdge(e.parent_type, e.child_type),
      });
    }
    return { nodes, links };
  }, [rawData, visibleTypes]);

  const nodeMap = useMemo(() => {
    const m = {};
    for (const n of forceGraphData.nodes) m[n.id] = n;
    return m;
  }, [forceGraphData.nodes]);

  const selectedNodeRels = useMemo(() => {
    if (!selectedNode) return { incoming: [], outgoing: [] };
    const incoming = [], outgoing = [];
    for (const l of forceGraphData.links) {
      const sid = typeof l.source === 'object' ? l.source.id : l.source;
      const tid = typeof l.target === 'object' ? l.target.id : l.target;
      if (sid === selectedNode.id) { const tn = nodeMap[tid]; if (tn) outgoing.push(tn); }
      if (tid === selectedNode.id) { const sn = nodeMap[sid]; if (sn) incoming.push(sn); }
    }
    return { incoming, outgoing };
  }, [selectedNode, forceGraphData.links, nodeMap]);

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const r = Math.max(4, 7 - Math.log10(forceGraphData.nodes.length + 1) * 1.5);
    const isSelected = selectedNode?.id === node.id;
    if (isSelected) {
      ctx.beginPath(); ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(255,235,59,0.45)'; ctx.fill();
    }
    ctx.beginPath(); ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = node.color; ctx.fill();
    if (isSelected) { ctx.strokeStyle = '#FDD835'; ctx.lineWidth = 2; ctx.stroke(); }
    if (globalScale > 1.2) {
      const fontSize = Math.min(12 / globalScale, 5);
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillStyle = '#333';
      let text = node.displayLabel;
      const maxLen = Math.max(10, Math.round(30 / globalScale));
      if (text.length > maxLen) text = text.slice(0, maxLen - 1) + '\u2026';
      ctx.fillText(text, node.x, node.y + r + 1);
    }
  }, [selectedNode, forceGraphData.nodes.length]);

  const linkCanvasObject = useCallback((link, ctx) => {
    const s = typeof link.source === 'object' ? link.source : null;
    const t = typeof link.target === 'object' ? link.target : null;
    if (!s || !t) return;
    const dx = t.x - s.x, dy = t.y - s.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;
    const ux = dx / len, uy = dy / len;
    const nodeR = Math.max(4, 7 - Math.log10(forceGraphData.nodes.length + 1) * 1.5);
    // Shorten line to stop at node edge
    const sx = s.x + ux * nodeR, sy = s.y + uy * nodeR;
    const tx = t.x - ux * nodeR, ty = t.y - uy * nodeR;

    ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(tx, ty);
    ctx.strokeStyle = link.cross ? EDGE_COLORS.cross : EDGE_COLORS.same;
    ctx.lineWidth = link.cross ? 1.2 : 0.4;
    ctx.globalAlpha = link.cross ? 0.7 : 0.2;
    ctx.stroke(); ctx.globalAlpha = 1;

    // Arrow on all edges
    const aL = link.cross ? 6 : 4;
    const aW = link.cross ? 3 : 2;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx - ux * aL - uy * aW, ty - uy * aL + ux * aW);
    ctx.lineTo(tx - ux * aL + uy * aW, ty - uy * aL - ux * aW);
    ctx.closePath();
    ctx.fillStyle = link.cross ? EDGE_COLORS.cross : EDGE_COLORS.same;
    ctx.globalAlpha = link.cross ? 0.8 : 0.35;
    ctx.fill(); ctx.globalAlpha = 1;
  }, [forceGraphData.nodes.length]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode((prev) => prev?.id === node.id ? null : node);
  }, []);

  const handleZoomIn = () => graphRef.current?.zoom(graphRef.current.zoom() * 1.4, 300);
  const handleZoomOut = () => graphRef.current?.zoom(graphRef.current.zoom() / 1.4, 300);
  const handleZoomFit = () => graphRef.current?.zoomToFit(400, 40);

  const handleToggleType = (type) => setVisibleTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  const handleShowAll = () => setVisibleTypes((prev) => { const n = {}; for (const k of Object.keys(prev)) n[k] = true; return n; });
  const handleHideAll = () => setVisibleTypes((prev) => { const n = {}; for (const k of Object.keys(prev)) n[k] = false; return n; });

  const navigateToNode = useCallback((node) => {
    setSelectedNode(node);
    if (graphRef.current && node.x != null && node.y != null) {
      graphRef.current.centerAt(node.x, node.y, 500);
      graphRef.current.zoom(3, 500);
    }
  }, []);

  const typeCounts = useMemo(() => {
    if (!rawData) return {};
    const c = {};
    for (const n of rawData.nodes) c[n.entity_type] = (c[n.entity_type] || 0) + 1;
    return c;
  }, [rawData]);

  const sortedTypes = useMemo(() => Object.keys(typeCounts).sort(), [typeCounts]);
  const visibleCount = forceGraphData.nodes.length;
  const totalCount = rawData?.node_count || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth fullScreen={isFullscreen}
      PaperProps={{ sx: isFullscreen ? {} : { width: '95vw', height: '90vh', maxWidth: '95vw' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountTree sx={{ color: blue[700] }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Entity Graph — {projectName || `Project ${projectId}`}
          </Typography>
          {rawData && (
            <Chip label={`${visibleCount} / ${totalCount} nodes · ${forceGraphData.links.length} edges`} size="small" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={() => setIsFullscreen((f) => !f)}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden', flex: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
            <CircularProgress size={36} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">Loading entity graph…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2, width: '100%' }}>{error}</Alert>}
        {rawData && !loading && (
          <>
            {/* Left sidebar: type filters */}
            <Box sx={{ width: 220, minWidth: 220, borderRight: '1px solid', borderColor: 'divider', overflow: 'auto', bgcolor: grey[50], p: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: grey[700] }}>Entity Types</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                <Button size="small" variant="text" onClick={handleShowAll} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5 }}>All</Button>
                <Button size="small" variant="text" onClick={handleHideAll} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.5 }}>None</Button>
              </Stack>
              <FormGroup>
                {sortedTypes.map((type) => (
                  <FormControlLabel key={type}
                    control={<Checkbox checked={!!visibleTypes[type]} onChange={() => handleToggleType(type)} size="small"
                      sx={{ py: 0.2, color: TYPE_COLORS_HEX[type], '&.Mui-checked': { color: TYPE_COLORS_HEX[type] } }} />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>{TYPE_SHORT[type] || type}</Typography>
                        <Chip label={typeCounts[type]} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }} />
                      </Stack>
                    }
                    sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { ml: 0.3 } }}
                  />
                ))}
              </FormGroup>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: grey[700] }}>Legend</Typography>
              {sortedTypes.map((type) => (
                <Stack key={type} direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TYPE_COLORS_HEX[type] || grey[500], flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.68rem', color: grey[700] }}>{humanize(type)}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
                <Box sx={{ width: 14, height: 2, bgcolor: EDGE_COLORS.cross, borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 600, color: orange[800] }}>Cross-source</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 14, height: 1, bgcolor: EDGE_COLORS.same, borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: grey[600] }}>Same source</Typography>
              </Stack>
            </Box>

            {/* Center: graph canvas */}
            <Box ref={containerRef} sx={{ flex: 1, position: 'relative', overflow: 'hidden', bgcolor: '#f5f7fa' }}>
              {forceGraphData.nodes.length > 0 && (
                <ForceGraph2D
                  ref={graphRef}
                  graphData={forceGraphData}
                  width={dimensions.width}
                  height={dimensions.height}
                  backgroundColor="#f5f7fa"
                  nodeCanvasObject={nodeCanvasObject}
                  nodeCanvasObjectMode={() => 'replace'}
                  linkCanvasObject={linkCanvasObject}
                  linkCanvasObjectMode={() => 'replace'}
                  onNodeClick={handleNodeClick}
                  onNodeDragEnd={(node) => { node.fx = node.x; node.fy = node.y; }}
                  nodeLabel={(node) => `${TYPE_SHORT[node.entity_type] || node.entity_type}\n${node.notation || ''}\n${node.label}`}
                  enableNodeDrag={true}
                  enableZoomInteraction={true}
                  enablePanInteraction={true}
                  cooldownTicks={200}
                  d3AlphaDecay={0.02}
                  d3VelocityDecay={0.3}
                  d3AlphaMin={0.001}
                  warmupTicks={50}
                  onEngineStop={() => { if (graphRef.current) graphRef.current.zoomToFit(400, 40); }}
                />
              )}
              {forceGraphData.nodes.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No entities visible. Enable some types in the sidebar.</Typography>
                </Box>
              )}
              <Paper elevation={2} sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.95)' }}>
                <Tooltip title="Zoom in" placement="left"><IconButton size="small" onClick={handleZoomIn}><ZoomIn fontSize="small" /></IconButton></Tooltip>
                <Divider />
                <Tooltip title="Zoom out" placement="left"><IconButton size="small" onClick={handleZoomOut}><ZoomOut fontSize="small" /></IconButton></Tooltip>
                <Divider />
                <Tooltip title="Fit to view" placement="left"><IconButton size="small" onClick={handleZoomFit}><CenterFocusStrong fontSize="small" /></IconButton></Tooltip>
              </Paper>
            </Box>

            {/* Right panel: selected node detail */}
            {selectedNode && (
              <Box sx={{ width: 320, minWidth: 320, borderLeft: '1px solid', borderColor: 'divider', overflow: 'auto', p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Node Detail</Typography>
                  <IconButton size="small" onClick={() => setSelectedNode(null)}><Close fontSize="small" /></IconButton>
                </Stack>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Chip label={humanize(selectedNode.entity_type)} size="small"
                    sx={{ fontWeight: 700, bgcolor: TYPE_COLORS_MUI[selectedNode.entity_type] || grey[600], color: '#fff', mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, wordBreak: 'break-word' }}>
                    {selectedNode.notation || selectedNode.name || selectedNode.label}
                  </Typography>
                  <Divider sx={{ my: 0.5 }} />
                  {selectedNode.notation && <Typography variant="caption" display="block" color="text.secondary"><b>Notation:</b> {selectedNode.notation}</Typography>}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-word' }}><b>Label:</b> {selectedNode.label || '-'}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary"><b>DB ID:</b> {selectedNode.dbId}</Typography>
                  {selectedNode.uri && (
                    <Tooltip title={selectedNode.uri}>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <b>URI:</b> {selectedNode.uri}
                      </Typography>
                    </Tooltip>
                  )}
                </Paper>
                {selectedNodeRels.incoming.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: green[700], display: 'block', mb: 0.5 }}>
                      ← Incoming ({selectedNodeRels.incoming.length})
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {selectedNodeRels.incoming.map((n, i) => (
                        <ListItemButton key={`in-${i}`} sx={{ py: 0.1, pl: 0.5 }} onClick={() => navigateToNode(n)}>
                          <Chip label={TYPE_SHORT[n.entity_type] || n.entity_type} size="small"
                            sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: TYPE_COLORS_MUI[n.entity_type] || grey[600], color: '#fff', mr: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.notation || n.label || n.name}
                          </Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
                {selectedNodeRels.outgoing.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: blue[700], display: 'block', mb: 0.5 }}>
                      → Outgoing ({selectedNodeRels.outgoing.length})
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {selectedNodeRels.outgoing.map((n, i) => (
                        <ListItemButton key={`out-${i}`} sx={{ py: 0.1, pl: 0.5 }} onClick={() => navigateToNode(n)}>
                          <Chip label={TYPE_SHORT[n.entity_type] || n.entity_type} size="small"
                            sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: TYPE_COLORS_MUI[n.entity_type] || grey[600], color: '#fff', mr: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.notation || n.label || n.name}
                          </Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
                {selectedNodeRels.incoming.length === 0 && selectedNodeRels.outgoing.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No relationships.</Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EntityGraphDialog;
